import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Col, Row, Typography, Input, Form, Select, Button } from 'antd';

import messages from 'i18n/messages/home';
import { skuPropertiesList, defaultType } from 'pages/qa-spec/constant';
import EditInputCountry from 'common/components/form/edit-input-country/EditInputCountry';
import { WrapperSelect } from 'common/components';
import { CountryEditorRender } from 'pages/qa-spec/components';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import {
  useCheckAllowEdit,
  useCheckAllowEditNBE,
  useCheckAllowViewNBE,
} from 'pages/qa-spec/hooks';

import SelectCategory from 'pages/home/ribbon/components/sections/product/SelectCategory';
import { generateIdsBySelectedKey } from 'pages/company-profile/components/tabs/system/category-management/utils';

import { useCheckPermissions, useGetEnumsByEnumName } from 'hooks';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';
import { SHOULD_ASSIGN_PRODUCT_CATEGORY } from 'static/Permission';
import sortByKey from 'utils/sortByKey';
import { sortBy } from 'lodash';
import classnames from 'classnames';
import './QaSpecHeaderProperty.less';

import { useGetSnapshotQaDataHeader } from './hook';
import SnapshotFormItem from '../qa-spec-tab/SnapshotFormItem';
import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { renderPopoverContent } from 'pages/qa-spec/utils';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { COMMENT_FIELDS } from 'static/QaSpecFields';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const formItemProps = {
  labelCol: {
    flex: '325px',
  },
};

const formRightItemProps = {
  labelCol: {
    flex: '150px',
  },
};

const QaSpecHeaderProperty = ({
  isEditable,
  qaSpecDataHeader,
  setCategoryList,
}) => {
  return (
    <>
      {isEditable ? (
        <RenderEditHeaderProperty
          qaSpecDataHeader={qaSpecDataHeader}
          setCategoryList={setCategoryList}
        />
      ) : (
        <Row className='qa-sku__view'>
          <RenderHeaderProperties qaSpecDataHeader={qaSpecDataHeader} />
        </Row>
      )}
    </>
  );
};

const hiddenFields = [
  'competitiveBrandEquivalencyType',
  'differencesAccepted',
  'competitiveProductBrandRetailPricingTarget',
  'testingTier',
];

const firstColumnFields = skuPropertiesList().filter((item) => item.id <= 8);
const secondColumnFields = skuPropertiesList().filter((item) => item.id > 8);

const RenderEditHeaderProperty = ({ setCategoryList, qaSpecDataHeader }) => {
  const { id: productId } = useParams();
  const intl = useIntl();
  const form = Form.useFormInstance();
  const hasPermissionEditQASpec = useCheckAllowEdit(productId);
  const hasPermissionEditNBE = useCheckAllowEditNBE();
  const hasPermissionViewNBE = useCheckAllowViewNBE();

  const filteredFirstColumn = hasPermissionViewNBE
    ? firstColumnFields
    : firstColumnFields.filter((field) => !hiddenFields.includes(field.value));
  const filteredSecondColumn = hasPermissionViewNBE
    ? secondColumnFields
    : secondColumnFields.filter((field) => !hiddenFields.includes(field.value));

  const [visibleModal, setVisibleModal] = useState(false);
  // Initialize state with the form's initial value for competitiveEquivalencyType
  const [competitiveEquivalencyType, setCompetitiveEquivalencyType] = useState(
    form.getFieldValue('competitiveBrandEquivalencyType')
  );

  // Ensure state sync with initial form values on load
  useEffect(() => {
    const initialType = form.getFieldValue('competitiveBrandEquivalencyType');
    setCompetitiveEquivalencyType(initialType);
  }, [form]);

  const handleCompetitiveEquivalencyTypeChange = (value) => {
    setCompetitiveEquivalencyType(value);
    if (value !== 'Similar Match Acceptable') {
      form.setFieldsValue({ differencesAccepted: null });
    }
  };

  const qaTypeEnums = useGetEnumsByEnumName('ProductOverviewTypeCodeEnum');

  const qaTierEnums = [
    {
      enumCode: 'TIER_1',
      enumDescription: 'Tier 1',
      enumDisplayName: 'Tier 1',
      enumValue: 'TIER_1',
    },
    {
      enumCode: 'TIER_2',
      enumDescription: 'Tier 2',
      enumDisplayName: 'Tier 2',
      enumValue: 'TIER_2',
    },
    {
      enumCode: 'TIER_3',
      enumDescription: 'Tier 3',
      enumDisplayName: 'Tier 3',
      enumValue: 'TIER_3',
    },
  ];

  const qaSmartLabelTemplateEnum = useGetEnumsByEnumName(
    'SmartLabelTemplateEnum'
  );

  const qaBrandEquivalencyTemplateEnum = [
    {
      enumCode: 'EXACT_MATCH',
      enumDescription: 'Exact Match',
      enumDisplayName: 'Exact Match',
      enumValue: 'Exact Match',
    },
    {
      enumCode: 'SIMILAR_MATCH_ACCEPTABLE',
      enumDescription: 'Similar Match Acceptable',
      enumDisplayName: 'Similar Match Acceptable',
      enumValue: 'Similar Match Acceptable',
    },
    {
      enumCode: 'NOT_APPLICABLE',
      enumDescription: 'Not Applicable',
      enumDisplayName: 'Not Applicable',
      enumValue: 'Not Applicable',
    },
  ];

  const countryCodeEnum = useGetEnumsByEnumName('CountryCodeEnum');

  const hasPermissionAssignCategory = useCheckPermissions(
    SHOULD_ASSIGN_PRODUCT_CATEGORY
  );

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId: qaSpecDataHeader?.productId,
  });

  const checkRequiredField = (fieldName) => {
    if (!requiredFields) return false;

    const isNormalFieldRequired = requiredFields.includes(
      `qaSpecification.${fieldName}`.toLowerCase()
    );

    const isTdcFieldRequired = requiredFields.includes(
      `dProperties.${fieldName}`.toLowerCase()
    );

    return isNormalFieldRequired || isTdcFieldRequired;
  };

  const handleSelectedCategory = (node, formValue) => {
    let params = {};

    if (node) {
      const categoryList = generateIdsBySelectedKey(node?.key);
      for (let i = 0; i < categoryList.length; i++) {
        params[`category${i + 1}Id`] = categoryList[i];
      }
    } else {
      for (let i = 0; i < 4; i++) {
        params[`category${i + 1}Id`] = null;
      }
    }

    setCategoryList(params);
    form.setFieldsValue({
      ...form.getFieldsValue(),
      ...formValue,
    });
  };

  const QASkuProperty = {
    selectType: {
      component: (
        <WrapperSelect showSearch allowClear={false} listHeight={350}>
          {sortByKey(qaTypeEnums, 'enumDisplayName').map((item) => {
            const valueOption = item.enumDisplayName ?? defaultType;
            return (
              <Option value={valueOption} key={valueOption}>
                {valueOption}
              </Option>
            );
          })}
        </WrapperSelect>
      ),
      selectProps: {
        options: sortBy(qaTypeEnums, ['displayName']),
        inputProps: {
          allowClear: true,
          showSearch: true,
        },
      },
    },
    selectTestingTier: {
      component: (
        <WrapperSelect showSearch allowClear={true} listHeight={350}>
          {sortByKey(qaTierEnums, 'enumDisplayName').map((item) => {
            const valueOption = item.enumDisplayName ?? defaultType;
            return (
              <Option value={valueOption} key={valueOption}>
                {valueOption}
              </Option>
            );
          })}
        </WrapperSelect>
      ),
      selectProps: {
        options: sortBy(qaTierEnums, ['displayName']),
        inputProps: {
          allowClear: true,
          showSearch: true,
        },
      },
    },
    inputDifferencesAccepted: {
      component: (
        <TextArea maxLength={500} showCount className='textarea-margin' />
      ),
    },
    selectSmartLabel: {
      component: (
        <WrapperSelect showSearch allowClear={true}>
          {sortByKey(qaSmartLabelTemplateEnum, 'enumDisplayName').map(
            (item) => {
              const valueOption = item.enumDisplayName ?? defaultType;
              return (
                <Option value={valueOption} key={valueOption}>
                  {valueOption}
                </Option>
              );
            }
          )}
        </WrapperSelect>
      ),
      selectProps: {
        options: sortBy(qaSmartLabelTemplateEnum, ['displayName']),
        inputProps: {
          allowClear: true,
          showSearch: true,
        },
      },
    },
    input: {
      component: <Input />,
    },
    inputRetailPricingTarget: {
      component: <Input maxLength={50} />,
    },
    disableInput: {
      component: <Input disabled={true} />,
    },
    countrySelect: {
      component: <EditInputCountry className='qa-sku-form__location' />,
      selectProps: {
        options: sortBy(countryCodeEnum, ['enumDisplayName']),
        inputProps: {
          allowClear: true,
          showSearch: true,
        },
      },
    },
    selectCompetitiveBrand: {
      component: (
        <WrapperSelect showSearch allowClear={true}>
          {qaBrandEquivalencyTemplateEnum.map((item) => {
            const valueOption = item.enumDisplayName ?? defaultType;
            return (
              <Option value={valueOption} key={valueOption}>
                {valueOption}
              </Option>
            );
          })}
        </WrapperSelect>
      ),
      selectProps: {
        options: sortBy(qaBrandEquivalencyTemplateEnum, ['displayName']),
        inputProps: {
          allowClear: true,
          showSearch: true,
        },
      },
    },
  };

  return (
    <>
      <div className={classnames('qaSpec-header-sku')}>
        <Row
          gutter={[16, 16]}
          justify='start'
          align='top'
          className='qaSpec-responsive-container'
          wrap
        >
          {/* Left Column (60%) */}
          <Col xs={24} sm={24} md={24} lg={15} xl={15}>
            {filteredFirstColumn.map((item, idx) => {
              const label = intl.formatMessage(messages[item.label]);
              const isRequired = checkRequiredField(item.value);

              // Fields to set as readonly
              const readonlyFields = [
                'competitiveProductBrandRetailPricingTarget',
                'differencesAccepted',
                'competitiveBrandEquivalencyType',
                'testingTier',
              ];

              const isFieldReadonly = (() => {
                if (hasPermissionEditQASpec) {
                  return (
                    !hasPermissionEditNBE && readonlyFields.includes(item.value)
                  );
                }

                if (hasPermissionEditNBE) {
                  return !readonlyFields.includes(item.value);
                }
                return true;
              })();

              const rules = isFieldReadonly
                ? [] // Remove validation for readonly fields
                : isRequired
                ? [{ required: true, message: `${label} is required` }]
                : [];

              if (item.value === 'competitiveBrandEquivalencyType') {
                return (
                  <Form.Item
                    key={`left-${idx}`}
                    label={
                      <TraceableCommentContainer
                        qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                        qaSpecFieldName={label}
                      >
                        <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                          <Text
                            className='label-qa-header'
                            style={{ fontWeight: 'bold' }}
                          >
                            {label}
                          </Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                    }
                    style={{ marginBottom: '6px' }}
                    name={item.value}
                    className={`qa-sku-form__${item.value}`}
                    rules={rules}
                    {...formItemProps}
                  >
                    <WrapperSelect
                      showSearch
                      allowClear
                      onChange={handleCompetitiveEquivalencyTypeChange}
                      disabled={false} // Keep selectable
                      style={
                        isFieldReadonly
                          ? { pointerEvents: 'none', opacity: 0.6 }
                          : {}
                      }
                    >
                      {qaBrandEquivalencyTemplateEnum.map((option) => (
                        <Option value={option.enumValue} key={option.enumValue}>
                          {option.enumDisplayName}
                        </Option>
                      ))}
                    </WrapperSelect>
                  </Form.Item>
                );
              }

              if (item.value === 'differencesAccepted') {
                if (competitiveEquivalencyType !== 'Similar Match Acceptable') {
                  return null;
                }
                return (
                  <Form.Item
                    key={`left-${idx}`}
                    label={
                      <TraceableCommentContainer
                        qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                        qaSpecFieldName={label}
                      >
                        <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                          <Text
                            className='label-qa-header'
                            style={{ fontWeight: 'bold' }}
                          >
                            {label}
                          </Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                    }
                    style={{ marginBottom: '6px' }}
                    name={item.value}
                    className={`qa-sku-form__${item.value}`}
                    rules={rules}
                    {...formItemProps}
                  >
                    <TextArea
                      maxLength={500}
                      showCount
                      className='textarea-margin'
                      readOnly={isFieldReadonly} // Set readOnly instead of disabled
                    />
                  </Form.Item>
                );
              }

              // Default rendering for other fields
              return (
                <Form.Item
                  key={`left-${idx}`}
                  label={
                    <TraceableCommentContainer
                      qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                      qaSpecFieldName={label}
                    >
                      <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                        <Text
                          className='label-qa-header'
                          style={{ fontWeight: 'bold' }}
                        >
                          {label}
                        </Text>
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  style={{ marginBottom: '6px' }}
                  name={item.value}
                  className={`qa-sku-form__${item.value}`}
                  rules={rules}
                  {...formItemProps}
                >
                  {React.cloneElement(
                    QASkuProperty[item.inputType]?.component,
                    { readOnly: isFieldReadonly } // Set readOnly for input components
                  )}
                </Form.Item>
              );
            })}
          </Col>

          {/* Right Column (40%) */}
          <Col xs={24} sm={24} md={24} lg={9} xl={9}>
            {filteredSecondColumn.map((item, idx) => {
              const label = intl.formatMessage(messages[item.label]);
              const isRequired = checkRequiredField(item.value);

              const readonlyFields = [
                'competitiveProductBrandRetailPricingTarget',
                'differencesAccepted',
                'competitiveBrandEquivalencyType',
                'testingTier',
              ];

              const isFieldReadonly = (() => {
                if (hasPermissionEditQASpec) {
                  return (
                    !hasPermissionEditNBE && readonlyFields.includes(item.value)
                  );
                }

                if (hasPermissionEditNBE) {
                  return !readonlyFields.includes(item.value);
                }
                return true;
              })();

              const rules = isFieldReadonly
                ? [] // Remove validation for readonly fields
                : isRequired
                ? [{ required: true, message: `${label} is required` }]
                : [];

              return (
                <Form.Item
                  key={`right-${idx}`}
                  label={
                    <TraceableCommentContainer
                      qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                      qaSpecFieldName={label}
                    >
                      <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                        <Text
                          className='label-qa-header'
                          style={{ fontWeight: 'bold' }}
                        >
                          {label}
                        </Text>
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  }
                  style={{ marginBottom: '6px', paddingRight: '5px' }}
                  name={item.value}
                  className={`qa-sku-form__${item.value}`}
                  rules={rules}
                  {...formRightItemProps}
                >
                  {React.cloneElement(
                    QASkuProperty[item.inputType]?.component,
                    {
                      readOnly: isFieldReadonly,
                    }
                  )}
                </Form.Item>
              );
            })}
            <Form.Item
              label={
                <Text
                  className='label-qa-header'
                  style={{ fontWeight: 'bold' }}
                ></Text>
              }
              className={`qa-sku-form__select-category`}
              style={{ marginBottom: '6px', paddingRight: '8px' }}
              {...formRightItemProps}
            >
              <Button
                type='primary'
                size='default'
                onClick={() => setVisibleModal(true)}
                disabled={!hasPermissionAssignCategory}
              >
                Select Category
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </div>
      <SelectCategory
        showEmptyCategory
        visible={visibleModal}
        setVisible={setVisibleModal}
        onClick={handleSelectedCategory}
      />
    </>
  );
};

const RenderHeaderProperties = ({ qaSpecDataHeader }) => {
  const intl = useIntl();

  const { id: productId } = useParams();

  const isToggleSnapshot = useCheckSnapshotForRetailer();
  const hasPermissionViewNBE = useCheckAllowViewNBE();
  const filteredFirstColumn = hasPermissionViewNBE
    ? firstColumnFields
    : firstColumnFields.filter((field) => !hiddenFields.includes(field.value));
  const filteredSecondColumn = hasPermissionViewNBE
    ? secondColumnFields
    : secondColumnFields.filter((field) => !hiddenFields.includes(field.value));

  const { data: snapshotData, isLoading } = useGetSnapshotQaDataHeader({
    productId,
    enabled: !!productId && isToggleSnapshot,
  });

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId: qaSpecDataHeader?.productId,
  });

  const [competitiveEquivalencyType, setCompetitiveEquivalencyType] = useState(
    qaSpecDataHeader?.competitiveBrandEquivalencyType
  );

  // Sync competitiveEquivalencyType on data change
  useEffect(() => {
    if (qaSpecDataHeader?.competitiveBrandEquivalencyType) {
      setCompetitiveEquivalencyType(
        qaSpecDataHeader.competitiveBrandEquivalencyType
      );
    }
  }, [qaSpecDataHeader]);

  const checkRequiredField = (fieldName) => {
    if (!requiredFields?.length) return false;

    const isNormalFieldRequired = requiredFields.includes(
      `qaSpecification.${fieldName}`.toLowerCase()
    );

    const isTdcFieldRequired = requiredFields.includes(
      `dProperties.${fieldName}`.toLowerCase()
    );

    return isNormalFieldRequired || isTdcFieldRequired;
  };

  const renderValueSku = (field, value) => {
    if (field.toLowerCase() === 'supplierlocation')
      return (
        <CountryEditorRender
          wrapperStyle={{
            display: 'inline-flex',
          }}
          value={value}
        />
      );

    return value;
  };

  const getQaSpecHeaderValue = (field, data) => {
    const isCategoryField = field.indexOf('category') > -1;
    const isTypeField = field === 'type';

    const value = isCategoryField ? data?.[field]?.categoryName : data?.[field];

    if (isTypeField && !value) return defaultType;

    return value;
  };

  return (
    <>
      <div className='qaSpec-header-sku'>
        <Row
          gutter={[16, 16]}
          justify='start'
          align='top'
          className='qaSpec-responsive-container'
          wrap // Ensures wrapping of columns
        >
          {/* First Column */}
          <Col xs={24} sm={24} md={24} lg={24} xl={15} className='left-column'>
            {filteredFirstColumn.map((item, idx) => {
              const supplierModifiedData =
                getQaSpecHeaderValue(item.value, qaSpecDataHeader) ?? '';

              const snapshotHeaderData =
                getQaSpecHeaderValue(item.value, snapshotData) ?? '';

              const needRequiredMark = checkRequiredField(item.value);

              const isDiffWithPreVersion =
                snapshotHeaderData !== supplierModifiedData;
              const shouldHighlight = isDiffWithPreVersion && isToggleSnapshot;

              if (
                item.value === 'differencesAccepted' &&
                competitiveEquivalencyType !== 'Similar Match Acceptable'
              ) {
                return null;
              }

              return (
                <SnapshotFormItem
                  key={`sfItem-${idx}`}
                  snapshotData={snapshotHeaderData}
                  isLoadingSnapshot={isLoading}
                  supplierModifiedData={supplierModifiedData}
                  renderPopoverContent={renderPopoverContent(
                    `${intl.formatMessage(messages[item.label])}`,
                    snapshotHeaderData,
                    supplierModifiedData
                  )}
                >
                  <Row
                    className='ant-row'
                    wrap={false}
                    gutter={[8, 0]}
                    key={idx}
                  >
                    {/* Right-aligned Label */}
                    <Col {...formItemProps.labelCol} className='qaSpec-label'>
                      <TraceableCommentContainer
                        qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                        qaSpecFieldName={intl.formatMessage(
                          messages[item.label]
                        )}
                      >
                        <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                          {needRequiredMark && (
                            <span className='required-mark'>* </span>
                          )}
                          <Typography.Text
                            className='label-qa-header'
                            ellipsis={{
                              tooltip: intl.formatMessage(messages[item.label]),
                            }}
                          >
                            {intl.formatMessage(messages[item.label])}
                          </Typography.Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                      :
                    </Col>

                    {/* Data Value */}
                    <Col flex='auto'>
                      {item.value === 'differencesAccepted' ? (
                        <Input.TextArea
                          value={supplierModifiedData}
                          readOnly
                          className='custom-textarea'
                          showCount
                        />
                      ) : (
                        <Typography.Text
                          ellipsis={{
                            tooltip: supplierModifiedData,
                          }}
                          className={shouldHighlight ? 'highlight-text' : ''}
                        >
                          {renderValueSku(item.value, supplierModifiedData)}
                        </Typography.Text>
                      )}
                    </Col>
                  </Row>
                </SnapshotFormItem>
              );
            })}
          </Col>

          {/* Second Column */}
          <Col xs={24} sm={24} md={24} lg={24} xl={9} className='right-column'>
            {filteredSecondColumn.map((item, idx) => {
              const supplierModifiedData =
                getQaSpecHeaderValue(item.value, qaSpecDataHeader) ?? '';

              const snapshotHeaderData =
                getQaSpecHeaderValue(item.value, snapshotData) ?? '';

              const needRequiredMark = checkRequiredField(item.value);

              const isDiffWithPreVersion =
                snapshotHeaderData !== supplierModifiedData;
              const shouldHighlight = isDiffWithPreVersion && isToggleSnapshot;

              return (
                <SnapshotFormItem
                  key={`sfItem2-${idx}`}
                  snapshotData={snapshotHeaderData}
                  isLoadingSnapshot={isLoading}
                  supplierModifiedData={supplierModifiedData}
                  renderPopoverContent={renderPopoverContent(
                    `${intl.formatMessage(messages[item.label])}`,
                    snapshotHeaderData,
                    supplierModifiedData
                  )}
                >
                  <Row
                    className='ant-row'
                    wrap={false}
                    gutter={[8, 0]}
                    key={idx}
                  >
                    {/* Right-aligned Label */}
                    <Col
                      {...formRightItemProps.labelCol}
                      className='qaSpec-label'
                    >
                      <TraceableCommentContainer
                        qaSpecName={COMMENT_FIELDS.header.qaSpecName}
                        qaSpecFieldName={intl.formatMessage(
                          messages[item.label]
                        )}
                      >
                        <QaSpecHelpContentLabel fieldPath={item?.fieldPath}>
                          {needRequiredMark && (
                            <span className='required-mark'>* </span>
                          )}
                          <Typography.Text
                            className='label-qa-header'
                            ellipsis={{
                              tooltip: intl.formatMessage(messages[item.label]),
                            }}
                          >
                            {intl.formatMessage(messages[item.label])}
                          </Typography.Text>
                        </QaSpecHelpContentLabel>
                      </TraceableCommentContainer>
                      :
                    </Col>

                    {/* Data Value */}
                    <Col flex='auto'>
                      <Typography.Text
                        ellipsis={{
                          tooltip: supplierModifiedData,
                        }}
                        className={shouldHighlight ? 'highlight-text' : ''}
                      >
                        {renderValueSku(item.value, supplierModifiedData)}
                      </Typography.Text>
                    </Col>
                  </Row>
                </SnapshotFormItem>
              );
            })}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default QaSpecHeaderProperty;
